import { normalizeRoutePathname } from "./adAccountIdUtils";

export const AD_PREVIEW_RESOLVE_PATH = "/v1/internal/ad_previews/resolve";
export const AD_PREVIEW_CACHE_CONTROL = "no-store, no-cache, must-revalidate, max-age=0, private";

const ABSOLUTE_URL_SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const PREVIEW_SEGMENT_PATTERN = /^[A-Za-z0-9._~-]+$/;

export function isAdPreviewPath(pathname: string): boolean {
  const normalizedPathname = normalizeRoutePathname(pathname);
  if (!normalizedPathname.startsWith("/previews/")) {
    return false;
  }

  const withoutPrefix = normalizedPathname.slice("/previews/".length);
  if (!withoutPrefix) {
    return false;
  }

  const previewId =
    withoutPrefix.endsWith(".data") ? withoutPrefix.slice(0, -".data".length) : withoutPrefix;
  return previewId.length > 0 && PREVIEW_SEGMENT_PATTERN.test(previewId);
}

export function sanitizeAdPreviewUrl(url: string): string {
  try {
    const isAbsoluteUrl = ABSOLUTE_URL_SCHEME_PATTERN.test(url);
    const isProtocolRelativeUrl = url.startsWith("//");
    const parsedUrl = new URL(url, "http://placeholder");

    if (!isAdPreviewPath(parsedUrl.pathname)) {
      return url;
    }

    parsedUrl.searchParams.delete("token");

    if (isAbsoluteUrl) {
      return parsedUrl.toString();
    }

    if (isProtocolRelativeUrl) {
      return `//${parsedUrl.host}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return sanitizeMalformedAdPreviewUrl(url);
  }
}

function sanitizeMalformedAdPreviewUrl(url: string): string {
  const [beforeHash, hash = ""] = splitOnce(url, "#");
  const [pathWithOrigin, rawQuery = ""] = splitOnce(beforeHash, "?");

  if (!pathWithOrigin.includes("/previews/")) {
    return url;
  }

  const sanitizedQuery = rawQuery
    .split("&")
    .filter((param) => param !== "token" && !param.startsWith("token="))
    .join("&");

  return `${pathWithOrigin}${sanitizedQuery ? `?${sanitizedQuery}` : ""}${hash ? `#${hash}` : ""}`;
}

function splitOnce(value: string, delimiter: string): [string, string?] {
  const delimiterIndex = value.indexOf(delimiter);
  if (delimiterIndex === -1) {
    return [value];
  }

  return [value.slice(0, delimiterIndex), value.slice(delimiterIndex + delimiter.length)];
}
